import * as THREE from "three";
import type { SceneHandle } from "../scene/renderer.js";
import { instantiate, loadModel, type ModelInstance } from "../assets/loader.js";
import { buildingTier } from "../assets/manifest.js";
import type { BaseState, BuildingView } from "./api.js";
import { CELL, cellToWorld, worldToCell } from "./grid.js";
import { Burst, WallRig, ZoneRig, makeHoloRing } from "./sceneParts.js";
import { ColonyLandscape } from "../scene/landscape.js";
import { Citizens } from "./Citizens.js";
import { ColonyDecorations } from "./ColonyDecorations.js";
import { useSession } from "../state/session.js";
import { COSMETICS } from "@zend/content";
import { applyCosmeticPaint } from "../assets/cosmeticPaint.js";

export interface Anchor {
  rowId: string;
  x: number;
  y: number;
  visible: boolean;
  /** Normalised depth (−1 near … 1 far) so nearer labels can stack above farther ones. */
  depth: number;
}

export interface GhostSpec {
  x: number;
  y: number;
  w: number;
  h: number;
  valid: boolean;
  rowId?: string;
  rot?: number;
}

export interface SceneCallbacks {
  /** A tap in view mode; `rowId` is null when nothing was hit. */
  onTap(rowId: string | null): void;
  /** In place/move mode the pointer positioned the ghost on a cell. */
  onGhostMove(x: number, y: number): void;
}

interface Entry {
  view: BuildingView;
  tier: 1 | 2 | 3;
  group: THREE.Group;
  pick: THREE.Mesh;
  instance: ModelInstance | null;
  holo: THREE.Mesh | null;
  height: number;
  token: number;
  restorePaint: (() => void) | null;
}

const PICK_LAYER = 1;
const TAP_PX = 8;
const TAP_MS = 450;

/**
 * Owns everything the base adds to the shared renderer: building instances by tier, the wall, zone rubble,
 * the selection ring, construction holos, placement ghost, picking and the camera focus tween (20-base.md).
 */
export class BaseScene {
  readonly root = new THREE.Group();
  private readonly entries = new Map<string, Entry>();
  private readonly wall = new WallRig();
  private readonly zones = new ZoneRig();
  private readonly landscape = new ColonyLandscape();
  private readonly citizens = new Citizens();
  private readonly decorations = new ColonyDecorations();
  private readonly ghost = new THREE.Group();
  private readonly ghostMat = new THREE.MeshStandardMaterial({
    color: 0x0b3b2a,
    emissive: new THREE.Color(0x22c55e),
    emissiveIntensity: 1.2,
    transparent: true,
    opacity: 0.45,
  });
  private readonly ghostBox: THREE.Mesh;
  private ghostSpec: GhostSpec | null = null;
  private selection: ModelInstance | null = null;
  private selectedRow: string | null = null;
  private selectionScale = 1;
  private bursts: Burst[] = [];
  private size = 24;
  private state: BaseState | null = null;
  private focusTarget: THREE.Vector3 | null = null;
  private readonly raycaster = new THREE.Raycaster();
  private readonly ground = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
  private readonly offFrame: () => void;
  private interactive = false;
  private draggingGhost = false;
  private down: { x: number; y: number; t: number; id: number } | null = null;
  private time = 0;
  private lastQuality: string | null = null;
  private disposed = false;
  private readonly canvas: HTMLElement;

  constructor(
    private readonly handle: SceneHandle,
    private readonly callbacks: SceneCallbacks,
  ) {
    this.canvas = handle.renderer.domElement;
    this.root.name = "base";
    this.root.add(this.wall.group, this.zones.group, this.landscape.group, this.citizens.group, this.decorations.group, this.ghost);
    this.ghostBox = new THREE.Mesh(new THREE.BoxGeometry(1, 0.5, 1), this.ghostMat);
    this.ghostBox.position.y = 0.25;
    this.ghost.add(this.ghostBox);
    this.ghost.visible = false;
    handle.scene.add(this.root);
    this.raycaster.layers.set(PICK_LAYER);
    void loadModel("prop.prop_selection_ring").then((m) => {
      if (this.disposed) return;
      this.selection = instantiate(m);
      this.selection.object.visible = false;
      this.selection.play("idle");
      this.root.add(this.selection.object);
      this.applySelection();
    });
    this.offFrame = handle.onFrame((dt) => this.tick(dt));
    this.canvas.addEventListener("pointerdown", this.onPointerDown, { capture: true });
    this.canvas.addEventListener("pointermove", this.onPointerMove, { capture: true });
    this.canvas.addEventListener("pointerup", this.onPointerUp, { capture: true });
    this.canvas.addEventListener("pointercancel", this.onPointerUp, { capture: true });
  }

  /** Hides the whole base (battle replays borrow the renderer and the camera). */
  setVisible(on: boolean): void {
    this.root.visible = on;
  }

  /** Enables the pointer handlers (the base route is active) or lets everything pass to the camera. */
  setInteractive(on: boolean): void {
    this.interactive = on;
  }

  /** Diffs the server state into the scene: new/removed buildings, tier swaps, timers, walls and zones. */
  update(state: BaseState): void {
    this.state = state;
    this.size = state.grid.size;
    const seen = new Set<string>();
    for (const view of state.buildings) {
      if (view.w === 0) continue; // the wall is rendered by the rig
      seen.add(view.id);
      const entry = this.entries.get(view.id);
      if (entry) this.refresh(entry, view);
      else this.add(view);
    }
    for (const [id, entry] of this.entries) {
      if (seen.has(id)) continue;
      this.remove(entry);
      this.entries.delete(id);
    }
    const open = new Set<string>();
    const rects = [state.grid.core, ...state.zones.filter((z) => z.state === "open").map((z) => z.rect)];
    for (const r of rects)
      for (let y = r.y; y < r.y + r.h; y++) for (let x = r.x; x < r.x + r.w; x++) open.add(`${x},${y}`);
    const wall = state.buildings.find((b) => b.buildingId === "bld.bastion_wall");
    this.wall.update(open, wall ? buildingTier(wall.level) : 0, this.size);
    this.zones.update(state.zones, this.size, this.handle.quality === "low");
    this.landscape.update(state);
    this.decorations.update(state);
    this.citizens.update(state, this.landscape.gardenCells);
    this.lastQuality = this.handle.quality;
    this.applySelection();
  }

  private add(view: BuildingView): void {
    const group = new THREE.Group();
    group.name = view.id;
    const pick = new THREE.Mesh(
      new THREE.BoxGeometry(view.w * CELL, 2.2, view.h * CELL),
      new THREE.MeshBasicMaterial({ visible: false }),
    );
    pick.position.y = 1.1;
    pick.layers.set(PICK_LAYER);
    pick.userData["rowId"] = view.id;
    group.add(pick);
    this.root.add(group);
    const entry: Entry = {
      view,
      tier: buildingTier(view.level),
      group,
      pick,
      instance: null,
      holo: null,
      height: 1.6,
      token: 0,
      restorePaint: null,
    };
    this.entries.set(view.id, entry);
    this.place(entry, view);
    this.loadTier(entry, entry.tier);
    this.syncHolo(entry, view);
  }

  private refresh(entry: Entry, view: BuildingView): void {
    const prev = entry.view;
    entry.view = view;
    if (
      prev.x !== view.x ||
      prev.y !== view.y ||
      prev.rot !== view.rot ||
      prev.w !== view.w ||
      prev.h !== view.h
    )
      this.place(entry, view);
    const tier = buildingTier(view.level);
    if (tier !== entry.tier) {
      entry.tier = tier;
      this.loadTier(entry, tier);
    }
    if (prev.skin !== view.skin) this.paint(entry);
    this.syncHolo(entry, view);
  }

  private place(entry: Entry, view: BuildingView): void {
    const { wx, wz } = cellToWorld(view.x, view.y, view.w, view.h, this.size);
    entry.group.position.set(wx, 0.02, wz);
    entry.group.rotation.y = -THREE.MathUtils.degToRad(view.rot);
    entry.pick.geometry.dispose();
    entry.pick.geometry = new THREE.BoxGeometry(view.w * CELL, 2.2, view.h * CELL);
    // The pick box stays axis-aligned to the effective footprint while the model rotates with the group.
    entry.pick.rotation.y = THREE.MathUtils.degToRad(view.rot);
  }

  private loadTier(entry: Entry, tier: 1 | 2 | 3): void {
    const token = ++entry.token;
    void loadModel(entry.view.buildingId, tier).then((model) => {
      if (this.disposed || entry.token !== token) return;
      entry.restorePaint?.();
      entry.restorePaint = null;
      if (entry.instance) {
        entry.instance.mixer.stopAllAction();
        entry.instance.mixer.uncacheRoot(entry.instance.object);
        entry.group.remove(entry.instance.object);
      }
      const inst = instantiate(model);
      inst.play("idle");
      entry.group.add(inst.object);
      entry.instance = inst;
      this.paint(entry);
      const box = new THREE.Box3().setFromObject(inst.object);
      entry.height = Math.max(1.2, box.max.y - Math.min(0, box.min.y));
    });
  }

  private paint(entry: Entry): void {
    entry.restorePaint?.();
    entry.restorePaint = null;
    const cosmetic = COSMETICS.find((candidate) => candidate.id === entry.view.skin && candidate.target === entry.view.buildingId);
    if (entry.instance && cosmetic)
      entry.restorePaint = applyCosmeticPaint(entry.instance.object, { ...cosmetic, colour: cosmetic.colour ?? null });
  }

  private syncHolo(entry: Entry, view: BuildingView): void {
    const running = view.state !== "idle";
    if (running && !entry.holo) {
      entry.holo = makeHoloRing(view.w, view.h);
      entry.group.add(entry.holo);
    } else if (!running && entry.holo) {
      entry.group.remove(entry.holo);
      (entry.holo.material as THREE.Material).dispose();
      entry.holo.geometry.dispose();
      entry.holo = null;
    }
  }

  private remove(entry: Entry): void {
    entry.token++;
    entry.restorePaint?.();
    entry.restorePaint = null;
    entry.instance?.mixer.stopAllAction();
    if (entry.instance) entry.instance.mixer.uncacheRoot(entry.instance.object);
    this.root.remove(entry.group);
    entry.pick.geometry.dispose();
    if (entry.holo) {
      (entry.holo.material as THREE.Material).dispose();
      entry.holo.geometry.dispose();
    }
  }

  setSelection(rowId: string | null): void {
    this.selectedRow = rowId;
    this.applySelection();
  }

  private applySelection(): void {
    if (!this.selection) return;
    const entry = this.selectedRow ? this.entries.get(this.selectedRow) : null;
    if (!entry) {
      this.selection.object.visible = false;
      return;
    }
    // The prop ring has radius 0.6; circumscribe the footprint with a little air.
    const s = (Math.hypot(entry.view.w, entry.view.h) * CELL * 0.5 + 0.35) / 0.6;
    this.selectionScale = s;
    this.selection.object.visible = true;
    this.selection.object.position.set(entry.group.position.x, 0.03, entry.group.position.z);
    this.selection.object.scale.set(s, 1, s);
  }

  setGhost(spec: GhostSpec | null): void {
    const previous = this.ghostSpec?.rowId;
    if (previous && previous !== spec?.rowId) {
      const entry = this.entries.get(previous);
      if (entry) this.place(entry, entry.view);
    }
    this.ghostSpec = spec;
    this.ghost.visible = !!spec;
    if (!spec) return;
    const { wx, wz } = cellToWorld(spec.x, spec.y, spec.w, spec.h, this.size);
    this.ghost.position.set(wx, 0.02, wz);
    this.ghostBox.scale.set(spec.w * CELL - 0.08, 1, spec.h * CELL - 0.08);
    this.ghostMat.color.set(spec.valid ? 0x0b3b2a : 0x3b0b0b);
    this.ghostMat.emissive.set(spec.valid ? 0x22c55e : 0xef4444);
    const moving = spec.rowId ? this.entries.get(spec.rowId) : null;
    if (moving) {
      moving.group.position.set(wx, 0.12, wz);
      moving.group.rotation.y = -THREE.MathUtils.degToRad(spec.rot ?? moving.view.rot);
    }
  }

  /** Tweens the orbit target to the building; the camera keeps its offset. */
  focus(rowId: string): void {
    const entry = this.entries.get(rowId);
    if (!entry) return;
    this.focusTarget = entry.group.position.clone();
  }

  /** Focuses a grid cell (zones, ghost) instead of a building. */
  focusCell(x: number, y: number, w = 1, h = 1): void {
    const { wx, wz } = cellToWorld(x, y, w, h, this.size);
    this.focusTarget = new THREE.Vector3(wx, 0, wz);
  }

  celebrate(rowId: string): void {
    const entry = this.entries.get(rowId);
    const origin = entry ? entry.group.position.clone().setY(entry.height * 0.6) : null;
    if (!origin) return;
    const burst = new Burst(origin);
    this.bursts.push(burst);
    this.root.add(burst.mesh);
  }

  /** Screen-space anchors (CSS px) above every building for the HTML labels. */
  anchors(): Anchor[] {
    const out: Anchor[] = [];
    const v = new THREE.Vector3();
    const rect = this.canvas.getBoundingClientRect();
    for (const entry of this.entries.values()) {
      v.copy(entry.group.position);
      v.y += entry.height + 0.3;
      v.project(this.handle.camera);
      const visible = v.z > -1 && v.z < 1;
      out.push({
        rowId: entry.view.id,
        x: ((v.x + 1) / 2) * rect.width,
        y: ((1 - v.y) / 2) * rect.height,
        visible,
        depth: v.z,
      });
    }
    return out;
  }

  /** Screen position (CSS px) above the centre of a cell rectangle, for zone labels. */
  projectCell(x: number, y: number, w: number, h: number, height = 0.5): Anchor {
    const { wx, wz } = cellToWorld(x, y, w, h, this.size);
    const v = new THREE.Vector3(wx, height, wz).project(this.handle.camera);
    const rect = this.canvas.getBoundingClientRect();
    return {
      rowId: "",
      x: ((v.x + 1) / 2) * rect.width,
      y: ((1 - v.y) / 2) * rect.height,
      visible: v.z > -1 && v.z < 1,
      depth: v.z,
    };
  }

  private pointerNdc(e: PointerEvent): THREE.Vector2 {
    const rect = this.canvas.getBoundingClientRect();
    return new THREE.Vector2(
      ((e.clientX - rect.left) / rect.width) * 2 - 1,
      -((e.clientY - rect.top) / rect.height) * 2 + 1,
    );
  }

  private pickRow(e: PointerEvent): string | null {
    this.raycaster.setFromCamera(this.pointerNdc(e), this.handle.camera);
    const hits = this.raycaster.intersectObjects(
      [...this.entries.values()].map((en) => en.pick),
      false,
    );
    const first = hits[0];
    return first ? String(first.object.userData["rowId"]) : null;
  }

  /** A placed decoration under the pointer, reported as `deco:<id>` (D70). */
  private pickDecoration(e: PointerEvent): string | null {
    this.raycaster.setFromCamera(this.pointerNdc(e), this.handle.camera);
    const id = this.decorations.pick(this.raycaster);
    return id ? `deco:${id}` : null;
  }

  /** Grid cell under the pointer for a footprint (clamped inside the grid). */
  private cellAt(e: PointerEvent, w: number, h: number): { x: number; y: number } | null {
    this.raycaster.setFromCamera(this.pointerNdc(e), this.handle.camera);
    const point = new THREE.Vector3();
    if (!this.raycaster.ray.intersectPlane(this.ground, point)) return null;
    return worldToCell(point.x, point.z, w, h, this.size);
  }

  private overGhost(e: PointerEvent): boolean {
    if (!this.ghostSpec || !this.ghost.visible) return false;
    const cell = this.cellAt(e, 1, 1);
    if (!cell) return false;
    const g = this.ghostSpec;
    return cell.x >= g.x && cell.x < g.x + g.w && cell.y >= g.y && cell.y < g.y + g.h;
  }

  private readonly onPointerDown = (e: PointerEvent) => {
    if (!this.interactive || !e.isPrimary) return;
    this.down = { x: e.clientX, y: e.clientY, t: performance.now(), id: e.pointerId };
    if (this.ghostSpec && this.overGhost(e)) {
      // Dragging the ghost must not orbit the camera: swallow the event before OrbitControls sees it.
      this.draggingGhost = true;
      e.stopImmediatePropagation();
      e.preventDefault();
      try {
        this.canvas.setPointerCapture(e.pointerId);
      } catch {
        /* capture is best effort */
      }
    }
  };

  private readonly onPointerMove = (e: PointerEvent) => {
    if (!this.draggingGhost || !this.ghostSpec) return;
    e.stopImmediatePropagation();
    const cell = this.cellAt(e, this.ghostSpec.w, this.ghostSpec.h);
    if (cell && (cell.x !== this.ghostSpec.x || cell.y !== this.ghostSpec.y))
      this.callbacks.onGhostMove(cell.x, cell.y);
  };

  private readonly onPointerUp = (e: PointerEvent) => {
    const down = this.down;
    this.down = null;
    if (this.draggingGhost) {
      this.draggingGhost = false;
      e.stopImmediatePropagation();
      try {
        this.canvas.releasePointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }
      return;
    }
    if (!this.interactive || !down || down.id !== e.pointerId) return;
    const moved = Math.hypot(e.clientX - down.x, e.clientY - down.y);
    if (moved > TAP_PX || performance.now() - down.t > TAP_MS) return;
    if (this.ghostSpec) {
      const cell = this.cellAt(e, this.ghostSpec.w, this.ghostSpec.h);
      if (cell) this.callbacks.onGhostMove(cell.x, cell.y);
      return;
    }
    this.callbacks.onTap(this.pickRow(e) ?? this.pickDecoration(e));
  };

  private tick(dt: number): void {
    this.time += dt;
    if (this.state && this.lastQuality !== null && this.lastQuality !== this.handle.quality)
      this.update(this.state);
    for (const entry of this.entries.values()) {
      entry.instance?.mixer.update(dt);
      if (entry.holo) {
        const pulse = 0.5 + 0.5 * Math.sin(this.time * 4);
        (entry.holo.material as THREE.MeshStandardMaterial).emissiveIntensity = 1.2 + pulse * 2;
        entry.holo.position.y = 0.3 + pulse * 0.35;
      }
    }
    this.selection?.mixer.update(dt);
    if (this.selection?.object.visible) {
      const s = this.selectionScale * (1 + 0.03 * Math.sin(this.time * 5));
      this.selection.object.scale.set(s, 1, s);
    }
    this.zones.tick(this.time);
    if (this.ghost.visible) this.ghostMat.opacity = 0.35 + 0.15 * Math.sin(this.time * 6);
    for (const b of this.bursts) b.tick(dt);
    this.bursts = this.bursts.filter((b) => {
      if (!b.done) return true;
      this.root.remove(b.mesh);
      b.dispose();
      return false;
    });
    // The camera belongs to whoever shows: while the base is hidden (map, replay) it is left alone.
    if (!this.root.visible) return;
    this.citizens.tick(dt, useSession.getState().settings?.reducedMotion ?? false);
    const controls = this.handle.controls;
    if (this.focusTarget) {
      const delta = this.focusTarget.clone().sub(controls.target);
      if (delta.length() < 0.02) this.focusTarget = null;
      const step = delta.multiplyScalar(Math.min(1, dt * 6));
      controls.target.add(step);
      this.handle.camera.position.add(step);
    }
    // Keep the orbit target on the plateau so panning never loses the base.
    const half = this.size / 2 + 2;
    const clampedX = THREE.MathUtils.clamp(controls.target.x, -half, half);
    const clampedZ = THREE.MathUtils.clamp(controls.target.z, -half, half);
    if (clampedX !== controls.target.x || clampedZ !== controls.target.z) {
      this.handle.camera.position.x += clampedX - controls.target.x;
      this.handle.camera.position.z += clampedZ - controls.target.z;
      controls.target.set(clampedX, 0, clampedZ);
    }
  }

  dispose(): void {
    this.disposed = true;
    this.offFrame();
    this.canvas.removeEventListener("pointerdown", this.onPointerDown, { capture: true });
    this.canvas.removeEventListener("pointermove", this.onPointerMove, { capture: true });
    this.canvas.removeEventListener("pointerup", this.onPointerUp, { capture: true });
    this.canvas.removeEventListener("pointercancel", this.onPointerUp, { capture: true });
    for (const entry of this.entries.values()) this.remove(entry);
    this.entries.clear();
    this.wall.dispose();
    this.zones.dispose();
    this.landscape.dispose();
    this.citizens.dispose();
    this.decorations.dispose();
    for (const b of this.bursts) b.dispose();
    this.ghostMat.dispose();
    this.ghostBox.geometry.dispose();
    this.handle.scene.remove(this.root);
  }
}
